// Coaching overlay shown during AR session warmup + tracking-loss recovery.
// Mirrors 8th Wall's xrextras.CoachingOverlay in spirit — a tinted backdrop
// with a clear instruction telling the user to move the phone slowly so
// SLAM converges before placement.
//
// Extended with a tracking-convergence progress bar + hint text (see
// @/ar/tracking-convergence) that turns the "black screen with a
// spinner" into a visible signal of how close ARCore is to producing
// a valid hit-test result. A "Place anyway" button becomes clickable
// after PLACE_ANYWAY_UNLOCK_MS so the user can force placement when
// tracking is stuck (poor lighting, glare, plain surface).
//
// The overlay itself is pointerEvents:'none' so canvas taps still
// pass through; the "Place anyway" button carries pointerEvents:'auto'
// so only that element is interactive.

interface CoachingOverlayProps {
  title: string
  subtitle?: string
  /** 0..1 tracking-convergence score. When omitted, no bar is shown
   *  (backwards-compatible for tracking-loss recovery which doesn't
   *  have a convergence signal). */
  progress?: number
  /** User-facing hint from computeTrackingConvergence — replaces
   *  subtitle when both are provided (progress signal is more
   *  actionable than the static subtitle). */
  hint?: string
  /** True once elapsed > PLACE_ANYWAY_UNLOCK_MS. Renders the button
   *  as clickable; before then it's not rendered at all. */
  placeAnywayUnlocked?: boolean
  /** Fires when the user taps "Place anyway" — App wires this to
   *  driver.forceReady() which flips session state to ready() and
   *  seeds a cachedHit 2m ahead of the camera. */
  onPlaceAnyway?: () => void
}

export function CoachingOverlay({
  title,
  subtitle,
  progress,
  hint,
  placeAnywayUnlocked,
  onPlaceAnyway,
}: CoachingOverlayProps) {
  const shownSubtitle = hint ?? subtitle
  return (
    <div
      data-testid="coaching-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        pointerEvents: 'none',
        color: 'white',
        textAlign: 'center',
        padding: 32,
      }}
    >
      <PhoneMotionHint />
      <div
        style={{
          fontSize: 17,
          fontWeight: 600,
          maxWidth: 'min(80vw, 360px)',
          lineHeight: 1.35,
        }}
      >
        {title}
      </div>
      {progress != null && <ProgressBar progress={progress} />}
      {shownSubtitle != null && (
        <div
          style={{
            fontSize: 13,
            opacity: 0.85,
            maxWidth: 'min(80vw, 320px)',
            lineHeight: 1.4,
          }}
        >
          {shownSubtitle}
        </div>
      )}
      {placeAnywayUnlocked === true && onPlaceAnyway != null && (
        <button
          type="button"
          data-testid="place-anyway-button"
          onClick={onPlaceAnyway}
          style={{
            marginTop: 6,
            pointerEvents: 'auto',
            padding: '10px 22px',
            border: '1px solid rgba(255,255,255,0.6)',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.08)',
            color: 'white',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Place anyway
        </button>
      )}
    </div>
  )
}

function ProgressBar({ progress }: { progress: number }) {
  const pct = Math.max(0, Math.min(1, progress)) * 100
  const label = pct < 100 ? `Tracking ${Math.round(pct)}%` : 'Ready'
  return (
    <div
      data-testid="coaching-progress"
      style={{
        width: 'min(70vw, 260px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <div
        style={{
          width: '100%',
          height: 6,
          background: 'rgba(255,255,255,0.18)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: pct + '%',
            height: '100%',
            background: 'white',
            transition: 'width 200ms ease-out',
          }}
        />
      </div>
      <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 0.4 }}>
        {label}
      </div>
    </div>
  )
}

/** Sliding-phone coaching hint — recreation of the classic WebAR
 *  "move your phone side to side" animation (the legacy 8th Wall
 *  coaching rendered this from its proprietary engine bundle, so
 *  there was no asset to reuse; this is our own SVG + CSS take).
 *  The motion demonstrates the translation ARCore's SLAM needs —
 *  a spinner communicated "loading", which scanning is not. */
function PhoneMotionHint() {
  return (
    <div
      data-testid="phone-motion-hint"
      style={{
        width: 120,
        height: 84,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Laid-forward look: the phone tilts away at the top and pumps
        // up/down; the +20% scale at the DOWN end reads as toward-the-
        // viewer — the forward/back sweep SLAM wants.
        perspective: 260,
      }}
    >
      <svg
        width="34"
        height="60"
        viewBox="0 0 34 60"
        fill="none"
        style={{ animation: 'av-phone-pump 2s ease-in-out infinite' }}
      >
        {/* Phone body */}
        <rect
          x="1.5"
          y="1.5"
          width="31"
          height="57"
          rx="6"
          stroke="white"
          strokeWidth="3"
        />
        {/* Camera dot */}
        <circle cx="17" cy="7.5" r="1.8" fill="white" />
        {/* Home bar */}
        <rect x="11" y="52" width="12" height="2.4" rx="1.2" fill="white" />
      </svg>
      <style>{`@keyframes av-phone-pump {
        0%   { transform: rotateX(52deg) translateY(-14px) scale(1) }
        50%  { transform: rotateX(52deg) translateY(14px) scale(1.2) }
        100% { transform: rotateX(52deg) translateY(-14px) scale(1) }
      }`}</style>
    </div>
  )
}
